import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Bath,
  Bed,
  BedDouble,
  Calendar,
  Car,
  ChevronLeft,
  ChevronRight,
  CookingPot,
  ExternalLink,
  Heart,
  House,
  Image as ImageIcon,
  Loader2,
  MapPin,
  MessageSquare,
  Pencil,
  PersonStanding,
  Plus,
  RefreshCw,
  SquareParking,
  Star,
  Trash2,
  Users,
  Waves,
  Wifi,
} from 'lucide-react';
import MapView from './components/MapView.jsx';
import HomePage from './pages/HomePage.jsx';
import { Button, Input, Textarea, Select, Card, Badge, Label, buttonClass } from './components/ui.jsx';
import { api } from './lib/api.js';
import { cn } from './lib/utils.js';

// ============ HELPERS ============

const parseImages = (imagesJson) => {
  if (!imagesJson) return [];
  try {
    const parsed = JSON.parse(imagesJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatEuro = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatBathrooms = (value) => {
  if (value === null || value === undefined) return null;
  return Number(value).toLocaleString('de-DE', { maximumFractionDigits: 1 });
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return null;
  const total = Math.max(0, Math.round(Number(minutes)));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours === 0) return `${rest} Min.`;
  if (rest === 0) return `${hours} Std.`;
  return `${hours} Std. ${rest} Min.`;
};

const formatCheckedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function ImageCarousel({ images, title }) {
  const [index, setIndex] = useState(0);
  const count = images.length;
  const firstImage = count > 0 ? images[0] : null;

  useEffect(() => {
    setIndex(0);
  }, [firstImage, count]);

  if (count === 0) {
    return (
      <div className="flex h-48 items-center justify-center bg-neutral-100 text-neutral-300">
        <ImageIcon size={28} strokeWidth={1.5} />
      </div>
    );
  }

  const current = ((index % count) + count) % count;
  const go = (delta) => setIndex((previous) => ((previous + delta) % count + count) % count);

  return (
    <div
      className="relative h-48 w-full overflow-hidden bg-neutral-100"
      tabIndex={count > 1 ? 0 : undefined}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') go(-1);
        if (event.key === 'ArrowRight') go(1);
      }}
    >
      <img
        src={images[current]}
        alt={`${title || 'Unterkunft'} – Bild ${current + 1}`}
        className="h-full w-full object-cover"
        loading={current === 0 ? 'eager' : 'lazy'}
      />
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Vorheriges Bild"
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 text-neutral-700 shadow-sm backdrop-blur transition hover:bg-white"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Nächstes Bild"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 text-neutral-700 shadow-sm backdrop-blur transition hover:bg-white"
          >
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-2 right-2 rounded-md bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
            {current + 1} / {count}
          </div>
        </>
      )}
    </div>
  );
}

// ============ PAGES ============

function TripPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [memberName, setMemberName] = useState('');
  const [member, setMember] = useState(null);
  const [listings, setListings] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshingId, setRefreshingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [travelTimes, setTravelTimes] = useState({});
  const [travelError, setTravelError] = useState('');
  const [availabilityJob, setAvailabilityJob] = useState(null);
  const [editingDeparture, setEditingDeparture] = useState(false);
  const [departureDraft, setDepartureDraft] = useState('');
  const [editingTripName, setEditingTripName] = useState(false);
  const [tripNameDraft, setTripNameDraft] = useState('');

  useEffect(() => {
    loadTrip();
  }, [code]);

  const loadTrip = async () => {
    try {
      const tripData = await api.get(`/api/trips/${code}`);
      setTrip(tripData);

      // Load member from localStorage
      const savedMember = localStorage.getItem(`member_${tripData.id}`);
      if (savedMember) {
        setMember(JSON.parse(savedMember));
      }

      await loadListings(tripData.id);
      if (tripData.departure) {
        // Do not block the initial render on geocoding/routing.
        loadTravelTimes(tripData.id);
      }
    } catch (err) {
      setError('Trip nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const loadListings = async (tripId) => {
    try {
      const data = await api.get(`/api/trips/${tripId}/listings`);
      setListings(data);
    } catch (err) {
      console.error('Error loading listings:', err);
    }
  };

  const loadTravelTimes = async (tripId) => {
    try {
      const data = await api.get(`/api/trips/${tripId}/travel-times`);
      const map = {};
      for (const item of data.items || []) {
        map[item.listing_id] = item;
      }
      setTravelTimes(map);
      setTravelError(data.warning || '');
      if (data.departure_lat != null && data.departure_lng != null) {
        setTrip((previous) =>
          previous
            ? {
                ...previous,
                departure_lat: data.departure_lat,
                departure_lng: data.departure_lng,
              }
            : previous
        );
      }
    } catch (err) {
      setTravelError(err.message);
    }
  };

  const saveDeparture = async (e) => {
    e.preventDefault();
    const value = departureDraft.trim();
    if (!value) {
      setEditingDeparture(false);
      return;
    }
    try {
      const updated = await api.patch(`/api/trips/${trip.id}`, { departure: value });
      setTrip(updated);
      setTravelTimes({});
      setListings((current) =>
        current.map((listing) => ({ ...listing, travel_minutes: null, travel_km: null }))
      );
      setEditingDeparture(false);
      setError('');
      await loadTravelTimes(trip.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const saveTripName = async (e) => {
    e.preventDefault();
    const value = tripNameDraft.trim();
    if (!value) {
      setEditingTripName(false);
      return;
    }
    try {
      const updated = await api.patch(`/api/trips/${trip.id}`, { name: value });
      setTrip(updated);
      setEditingTripName(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const checkAvailability = async () => {
    setError('');
    try {
      const job = await api.post(`/api/trips/${trip.id}/check-availability`, {});
      setAvailabilityJob(job);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (!availabilityJob?.running || !trip) return undefined;

    const interval = setInterval(async () => {
      try {
        const job = await api.get(`/api/trips/${trip.id}/check-availability`);
        setAvailabilityJob(job);
        if (!job.running) {
          clearInterval(interval);
          await loadListings(trip.id);
        }
      } catch (err) {
        console.error('Error polling availability:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [availabilityJob?.running, trip?.id]);

  const joinTrip = async (e) => {
    e.preventDefault();
    if (!memberName.trim()) return;

    try {
      const newMember = await api.post(`/api/trips/${trip.id}/members`, {
        name: memberName.trim(),
      });
      localStorage.setItem(`member_${trip.id}`, JSON.stringify(newMember));
      setMember(newMember);
    } catch (err) {
      setError(err.message);
    }
  };

  const addListing = async (e) => {
    e.preventDefault();
    if (!newUrl.trim() || isAdding) return;

    setIsAdding(true);
    setError('');
    try {
      await api.post(`/api/trips/${trip.id}/listings`, {
        url: newUrl.trim(),
        added_by: member?.id,
      });
      setNewUrl('');
      await loadListings(trip.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAdding(false);
    }
  };

  const toggleVote = async (listingId) => {
    if (!member) return;
    try {
      await api.post(`/api/listings/${listingId}/vote`, { member_id: member.id });
      await loadListings(trip.id);
    } catch (err) {
      console.error('Error toggling vote:', err);
    }
  };

  const updateAvailability = async (listingId, status) => {
    try {
      await api.patch(`/api/listings/${listingId}`, { availability_status: status });
      await loadListings(trip.id);
    } catch (err) {
      console.error('Error updating availability:', err);
    }
  };

  const deleteListing = async (listingId) => {
    if (!confirm('Wirklich löschen?')) return;
    try {
      await api.delete(`/api/listings/${listingId}`);
      await loadListings(trip.id);
    } catch (err) {
      console.error('Error deleting listing:', err);
    }
  };

  const refreshListing = async (listingId) => {
    setRefreshingId(listingId);
    setError('');
    try {
      await api.post(`/api/listings/${listingId}/refresh`);
      await loadListings(trip.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <span className="inline-flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 size={16} className="animate-spin" /> Lädt...
        </span>
      </div>
    );
  }

  if (error && !trip) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4">
        <p className="text-sm text-red-600">{error}</p>
        <Link to="/" className={buttonClass('secondary', 'sm')}>
          <ArrowLeft size={14} /> Zurück
        </Link>
      </div>
    );
  }

  // Not joined yet
  if (!member) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card className="p-6">
          <h1 className="text-xl font-semibold tracking-tight">{trip?.name}</h1>
          <div className="mt-3 space-y-1.5 text-sm text-neutral-500">
            {trip?.anreise && (
              <p className="flex items-center gap-2">
                <Calendar size={14} /> Anreise: {formatDate(trip.anreise)}
              </p>
            )}
            {trip?.abreise && (
              <p className="flex items-center gap-2">
                <Calendar size={14} /> Abreise: {formatDate(trip.abreise)}
              </p>
            )}
            {trip?.departure && (
              <p className="flex items-center gap-2">
                <MapPin size={14} /> Abfahrt: {trip.departure}
              </p>
            )}
          </div>
          <form onSubmit={joinTrip} className="mt-6 space-y-4 border-t border-neutral-100 pt-6">
            <div className="space-y-1.5">
              <Label>Dein Name</Label>
              <Input
                type="text"
                value={memberName}
                onChange={(e) => setMemberName(e.target.value)}
                placeholder="Wie heißt du?"
                required
              />
            </div>
            <Button type="submit" className="w-full">
              Beitreten
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // Joined - show full interface
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-neutral-200 pb-6">
        <div className="min-w-0 space-y-2">
          {editingTripName ? (
            <form className="flex items-center gap-2" onSubmit={saveTripName}>
              <Input
                type="text"
                className="max-w-xs"
                value={tripNameDraft}
                onChange={(e) => setTripNameDraft(e.target.value)}
                autoFocus
                required
              />
              <Button type="submit" size="sm">
                Speichern
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setEditingTripName(false)}>
                Abbrechen
              </Button>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="truncate text-2xl font-semibold tracking-tight">{trip?.name}</h1>
              <button
                type="button"
                onClick={() => {
                  setEditingTripName(true);
                  setTripNameDraft(trip?.name || '');
                }}
                title="Titel bearbeiten"
                className="rounded-md p-1.5 text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-700"
              >
                <Pencil size={14} />
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500">
            {trip?.anreise && trip?.abreise && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={14} /> {formatDate(trip.anreise)} – {formatDate(trip.abreise)}
              </span>
            )}
            {trip?.departure && !editingDeparture && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={14} /> {trip.departure}
                <button
                  type="button"
                  className="text-xs font-medium text-blue-600 hover:underline"
                  onClick={() => {
                    setDepartureDraft(trip.departure || '');
                    setEditingDeparture(true);
                  }}
                >
                  ändern
                </button>
              </span>
            )}
            {!trip?.departure && !editingDeparture && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:underline"
                onClick={() => {
                  setDepartureDraft('');
                  setEditingDeparture(true);
                }}
              >
                <MapPin size={14} /> Abfahrtsort festlegen
              </button>
            )}
          </div>
          {editingDeparture && (
            <form className="flex items-center gap-2 pt-1" onSubmit={saveDeparture}>
              <Input
                type="text"
                className="max-w-xs"
                value={departureDraft}
                onChange={(e) => setDepartureDraft(e.target.value)}
                placeholder="z.B. Dresden"
                autoFocus
                required
              />
              <Button type="submit" size="sm">
                Speichern
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setEditingDeparture(false)}>
                Abbrechen
              </Button>
            </form>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-neutral-500">{listings.length} Unterkünfte</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              localStorage.removeItem(`member_${trip.id}`);
              setMember(null);
            }}
          >
            Verlassen
          </Button>
        </div>
      </header>

      <form onSubmit={addListing} className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Input
          type="url"
          className="flex-1"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="Airbnb-Link einfügen..."
          required
        />
        <Button type="submit" disabled={isAdding}>
          {isAdding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          {isAdding ? 'Lade Daten...' : 'Hinzufügen'}
        </Button>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={checkAvailability}
          disabled={availabilityJob?.running || !trip?.anreise || !trip?.abreise}
          title={
            !trip?.anreise || !trip?.abreise
              ? 'Für die Prüfung werden Reisedaten benötigt'
              : 'Verfügbarkeit und Preise für die Reisedaten prüfen'
          }
        >
          {availabilityJob?.running ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          {availabilityJob?.running
            ? `Prüfe ${availabilityJob.done}/${availabilityJob.total}...`
            : 'Verfügbarkeit prüfen'}
        </Button>
        {availabilityJob?.running && (
          <span className="text-xs text-neutral-500">
            Airbnb wird abgefragt – das kann etwas dauern.
          </span>
        )}
        {!availabilityJob?.running && availabilityJob?.finishedAt && (
          <span className="text-xs text-neutral-500">
            Zuletzt geprüft: {formatCheckedAt(availabilityJob.finishedAt)}
          </span>
        )}
        {!availabilityJob?.running &&
          availabilityJob?.results?.some((result) => result.status === 'error') && (
            <span className="text-xs text-amber-600">
              {availabilityJob.results.filter((result) => result.status === 'error').length}{' '}
              Unterkunft(en) konnten nicht geprüft werden.
            </span>
          )}
        {travelError && <span className="text-xs text-amber-600">{travelError}</span>}
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <aside className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
              <MapPin size={14} /> Karte
            </h2>
            <span className="text-xs text-neutral-500">{listings.length} Unterkünfte</span>
          </div>
          <MapView listings={listings} trip={trip} travelTimes={travelTimes} />
        </aside>

        <section>
          {listings.length === 0 ? (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-10 text-center">
              <House size={28} strokeWidth={1.5} className="mx-auto text-neutral-300" />
              <p className="mt-3 text-sm text-neutral-500">
                Noch keine Unterkünfte. Füge die erste hinzu.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {listings.map((listing) => {
                const images = parseImages(listing.images_json);
                const gallery = images.length > 0 ? images : listing.image ? [listing.image] : [];
                const priceNight = listing.price_night ? Number(listing.price_night) : null;
                const priceTotal = listing.price_total ? Number(listing.price_total) : null;
                const perPersonNight = priceNight && listing.guests ? priceNight / listing.guests : null;
                const perPersonTotal = priceTotal && listing.guests ? priceTotal / listing.guests : null;
                const bathroomLabel = formatBathrooms(listing.bathrooms);
                const travel = travelTimes[listing.id];
                const hasVoted = listing.votes?.includes(member.id);

                return (
                  <article
                    key={listing.id}
                    className="overflow-hidden rounded-lg border border-neutral-200 bg-white transition hover:border-neutral-300"
                  >
                    <ImageCarousel images={gallery} title={listing.title} />

                    <div className="space-y-3 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold leading-snug text-neutral-900">
                          {listing.title || 'Ohne Titel'}
                        </h3>
                        {listing.property_type && <Badge>{listing.property_type}</Badge>}
                      </div>

                      {listing.location && (
                        <p className="flex items-center gap-1.5 text-xs text-neutral-500">
                          <MapPin size={12} /> {listing.location}
                        </p>
                      )}

                      {priceNight ? (
                        <div className="space-y-0.5">
                          <p className="text-lg font-semibold tracking-tight text-neutral-900">
                            {formatEuro(priceNight)}{' '}
                            <span className="text-xs font-normal text-neutral-500">Ø / Nacht</span>
                          </p>
                          {perPersonNight && (
                            <p className="text-xs text-neutral-500">
                              {formatEuro(perPersonNight)} pro Person / Nacht
                            </p>
                          )}
                          {priceTotal && (
                            <p className="text-xs text-neutral-500">
                              {formatEuro(priceTotal)} gesamt
                              {perPersonTotal ? ` · ${formatEuro(perPersonTotal)} pro Person` : ''}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-neutral-400">Preis auf Airbnb prüfen</p>
                      )}

                      <div className="flex flex-wrap items-center gap-1.5">
                        {listing.guests && (
                          <Badge>
                            <Users size={12} /> {listing.guests} Gäste
                          </Badge>
                        )}
                        {listing.bedrooms && (
                          <Badge>
                            <BedDouble size={12} /> {listing.bedrooms} Schlafzimmer
                          </Badge>
                        )}
                        {listing.beds && (
                          <Badge>
                            <Bed size={12} /> {listing.beds} Betten
                          </Badge>
                        )}
                        {bathroomLabel && (
                          <Badge>
                            <Bath size={12} /> {bathroomLabel} Bäder
                          </Badge>
                        )}
                        {travel?.minutes != null && (
                          <Badge className="border-neutral-300 bg-white text-neutral-900">
                            <Car size={12} />
                            {formatDuration(travel.minutes)}
                            {travel.km != null
                              ? ` · ${Number(travel.km).toLocaleString('de-DE', {
                                  maximumFractionDigits: 1,
                                })} km`
                              : ''}
                          </Badge>
                        )}
                      </div>

                      {listing.rating && (
                        <p className="flex items-center gap-1.5 text-sm text-neutral-700">
                          <Star size={14} className="fill-amber-400 text-amber-400" />
                          {Number(listing.rating).toLocaleString('de-DE')}
                          {listing.review_count > 0 && (
                            <span className="text-neutral-500">
                              ({listing.review_count} Bewertungen)
                            </span>
                          )}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-1.5">
                        {listing.has_wifi && (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                            <Wifi size={12} /> WLAN
                          </span>
                        )}
                        {listing.has_kitchen && (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                            <CookingPot size={12} /> Küche
                          </span>
                        )}
                        {listing.has_pool && (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                            <Waves size={12} /> Pool
                          </span>
                        )}
                        {listing.has_whirlpool && (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                            <Bath size={12} /> Whirlpool
                          </span>
                        )}
                        {listing.has_sauna && (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                            <PersonStanding size={12} /> Sauna
                          </span>
                        )}
                        {listing.has_parking && (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                            <SquareParking size={12} /> Parkplatz
                          </span>
                        )}
                      </div>

                      {listing.description && (
                        <details className="group">
                          <summary className="cursor-pointer select-none text-xs font-medium text-neutral-500 transition hover:text-neutral-900">
                            Beschreibung
                          </summary>
                          <p className="mt-2 text-sm leading-relaxed text-neutral-600">
                            {listing.description}
                          </p>
                        </details>
                      )}

                      <div className="flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
                        <Select
                          className="h-8 text-xs"
                          value={listing.availability_status || 'unknown'}
                          onChange={(e) => updateAvailability(listing.id, e.target.value)}
                        >
                          <option value="unknown">Unklar</option>
                          <option value="available">Verfügbar</option>
                          <option value="unavailable">Nicht verfügbar</option>
                        </Select>
                        <a
                          href={listing.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={buttonClass('secondary', 'sm')}
                        >
                          Auf Airbnb <ExternalLink size={12} />
                        </a>
                        {listing.availability_checked_at && (
                          <span className="text-xs text-neutral-400">
                            Stand: {formatCheckedAt(listing.availability_checked_at)}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => toggleVote(listing.id)}
                          className={cn(
                            buttonClass('secondary', 'sm'),
                            hasVoted &&
                              'border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100'
                          )}
                        >
                          <Heart
                            size={14}
                            className={hasVoted ? 'fill-rose-600 text-rose-600' : 'text-neutral-400'}
                          />
                          {listing.vote_count}
                        </button>
                        <Link
                          to={`/listing/${listing.id}`}
                          className={buttonClass('secondary', 'sm')}
                        >
                          <MessageSquare size={14} /> Kommentare
                        </Link>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => refreshListing(listing.id)}
                          disabled={refreshingId === listing.id}
                          title="Daten von Airbnb neu laden"
                        >
                          {refreshingId === listing.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <RefreshCw size={14} />
                          )}
                          Aktualisieren
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteListing(listing.id)}
                          title="Löschen"
                          className="text-neutral-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get listing details
        const listings = await api.get('/api/trips'); // Need to find trip first
        // For now, we'll get listing from any trip - this needs improvement
        // Simplified: load comments directly
        const commentsData = await api.get(`/api/listings/${id}/comments`);
        setComments(commentsData);

        // Get member from any trip
        for (const trip of listings) {
          const savedMember = localStorage.getItem(`member_${trip.id}`);
          if (savedMember) {
            setMember(JSON.parse(savedMember));
            break;
          }
        }
      } catch (err) {
        console.error('Error loading detail:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !member) return;

    try {
      const comment = await api.post(`/api/listings/${id}/comments`, {
        member_id: member.id,
        text: newComment.trim(),
      });
      setComments([...comments, comment]);
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/api/comments/${commentId}`);
      setComments(comments.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <span className="inline-flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 size={16} className="animate-spin" /> Lädt...
        </span>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft size={14} /> Zurück
      </Button>

      <h1 className="mt-6 flex items-center gap-2 text-xl font-semibold tracking-tight">
        <MessageSquare size={18} /> Kommentare
      </h1>

      <div className="mt-6 space-y-6">
        <div className="space-y-3">
          {comments.length === 0 ? (
            <p className="rounded-lg border border-dashed border-neutral-200 bg-white p-6 text-center text-sm text-neutral-500">
              Noch keine Kommentare.
            </p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="rounded-lg border border-neutral-200 bg-white p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-neutral-900">
                    {comment.member_name || 'Unbekannt'}
                  </span>
                  <span className="shrink-0 text-xs text-neutral-400">
                    {new Date(comment.created_at).toLocaleString('de-DE')}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{comment.text}</p>
                {member && member.id === comment.member_id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                    onClick={() => deleteComment(comment.id)}
                  >
                    <Trash2 size={14} /> Löschen
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        {member ? (
          <form onSubmit={addComment} className="space-y-3">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Dein Kommentar..."
              rows="3"
              required
            />
            <Button type="submit">Kommentieren</Button>
          </form>
        ) : (
          <p className="rounded-lg border border-dashed border-neutral-200 bg-white p-4 text-sm text-neutral-500">
            Melde dich im Trip an, um zu kommentieren.
          </p>
        )}
      </div>
    </div>
  );
}

// ============ APP ============

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/trip/:code" element={<TripPage />} />
        <Route path="/listing/:id" element={<ListingDetailPage />} />
      </Routes>
    </Router>
  );
}
